"use client"

import { useEvent } from "@/app/hooks/use-event"
import { CloseIcon } from "@/app/icons/close"
import { SuccessCheckIcon } from "@/app/icons/success-check"
import { Trans } from "@lingui/react/macro"
import { Button, sonnerToast } from "@rarible_int/anaconda_ui_components"

import { truncateSolanaAddress } from "./gacha-gift-address"
import {
  gachaSentToastContainerClassName,
  gachaSentToastSonnerOptions,
  gachaSentToastSubtitleClassName,
} from "./gacha-sent-toast-styles"

type GachaGiftSentToastProps = {
  id: string | number
  packImage: string
  quantity: number
  recipientAddress: string
}

export function GachaGiftSentToast({
  id,
  packImage,
  quantity,
  recipientAddress,
}: GachaGiftSentToastProps) {
  const handleClose = useEvent(() => {
    sonnerToast.dismiss(id)
  })

  const shortAddress = truncateSolanaAddress(recipientAddress)
  const message = `${quantity} packs sent to ${shortAddress}`

  return (
    <div
      data-testid="gacha-gift-sent-toast"
      className={gachaSentToastContainerClassName}
    >
      <div className="flex min-w-0 items-center gap-2">
        <SuccessCheckIcon className="shrink-0" />
        <div className="h-8 w-8 shrink-0 overflow-hidden rounded bg-foreground/[0.04]">
          <img
            src={packImage}
            alt=""
            className="h-full w-full object-contain"
          />
        </div>

        <div className="flex min-w-0 flex-col gap-1">
          <p className="font-semibold text-foreground">
            <Trans>Gift sent</Trans>
          </p>
          <p className={gachaSentToastSubtitleClassName} title={message}>
            {quantity} <Trans>packs sent to</Trans>{" "}
            <span className="font-mono">{shortAddress}</span>
          </p>
        </div>
      </div>

      <Button size="icon" variant="ghost" onClick={handleClose}>
        <CloseIcon className="[&&]:h-3 [&&]:w-3" />
      </Button>
    </div>
  )
}

export function showGachaGiftSentToast({
  packImage,
  quantity,
  recipientAddress,
}: Omit<GachaGiftSentToastProps, "id">) {
  sonnerToast.dismiss()
  sonnerToast.custom(
    (id) => (
      <GachaGiftSentToast
        id={id}
        packImage={packImage}
        quantity={quantity}
        recipientAddress={recipientAddress}
      />
    ),
    gachaSentToastSonnerOptions,
  )
}
